/**
 * Gamification Service API Contracts
 * Request/Response schemas for points, tiers, leaderboards, achievements, and challenges
 *
 * Volume: 50K-200K daily transactions (points awarded per scan/purchase)
 * Redis:  gam:lb:{type}:{period} sorted set TTL 86400s (24h)
 *         gam:points:{customerId} INCRBY no TTL
 *         gam:tier:{customerId} TTL 3600s (1h)
 */

import { z } from 'zod';

import { BooleanFromString } from '../common';

// ============================================================================
// Tier Schemas
// ============================================================================

export const TierSchema = z.enum(['bronze', 'silver', 'gold', 'platinum']);

export type Tier = z.infer<typeof TierSchema>;

export const TierDetailSchema = z.object({
  tier: TierSchema,
  pointsRequired: z.number().int().nonnegative(),
  pointsToNextTier: z.number().int().nonnegative().nullable(),
  benefits: z.array(z.string()),
  multiplier: z.number().positive(), // points earning multiplier
  color: z.string().max(20), // hex color for display
});

export type TierDetail = z.infer<typeof TierDetailSchema>;

// ============================================================================
// Customer Points Profile
// ============================================================================

export const CustomerPointsProfileSchema = z.object({
  customerId: z.string(),
  currentTier: TierSchema,
  totalLifetimePoints: z.number().int().nonnegative(),
  currentBalance: z.number().int().nonnegative(),
  pointsToNextTier: z.number().int().nonnegative().nullable(),
  tierExpiresAt: z.string().datetime().nullable(),
  totalAchievements: z.number().int().nonnegative(),
  totalBadges: z.number().int().nonnegative(),
  totalChallengesCompleted: z.number().int().nonnegative(),
  rank: z.number().int().positive().nullable(), // leaderboard rank, null if not ranked
  updatedAt: z.string().datetime(),
});

export type CustomerPointsProfile = z.infer<typeof CustomerPointsProfileSchema>;

// ============================================================================
// Points History
// ============================================================================

export const PointsTransactionSchema = z.object({
  id: z.string().uuid(),
  customerId: z.string(),
  type: z.enum(['earned', 'redeemed', 'expired', 'adjusted']),
  points: z.number().int(),
  balanceAfter: z.number().int().nonnegative(),
  reason: z.string(),
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  multiplierApplied: z.number().positive(),
  expiresAt: z.string().datetime().nullable(),
  timestamp: z.string().datetime(),
});

export type PointsTransaction = z.infer<typeof PointsTransactionSchema>;

export const GetPointsHistoryQuerySchema = z.object({
  customerId: z.string(),
  type: z.enum(['earned', 'redeemed', 'expired', 'adjusted']).optional(),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetPointsHistoryQuery = z.infer<typeof GetPointsHistoryQuerySchema>;

// ============================================================================
// Leaderboard
// ============================================================================

export const LeaderboardTypeSchema = z.enum(['daily', 'weekly', 'monthly', 'all_time']);

export type LeaderboardType = z.infer<typeof LeaderboardTypeSchema>;

export const LeaderboardEntrySchema = z.object({
  rank: z.number().int().positive(),
  customerId: z.string(),
  displayName: z.string().max(100),
  tier: TierSchema,
  points: z.number().int().nonnegative(),
  avatarUrl: z.string().url().nullable(),
  /** Whether this entry is the requesting customer */
  isCurrentUser: z.boolean().optional(),
});

export type LeaderboardEntry = z.infer<typeof LeaderboardEntrySchema>;

export const GetLeaderboardQuerySchema = z.object({
  type: LeaderboardTypeSchema.default('weekly'),
  limit: z.coerce.number().int().positive().max(100).default(50),
  /** Include the requesting customer's rank even if outside top N */
  includeMyRank: BooleanFromString.default(true),
});

export type GetLeaderboardQuery = z.infer<typeof GetLeaderboardQuerySchema>;

export const LeaderboardResponseSchema = z.object({
  type: LeaderboardTypeSchema,
  period: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
  entries: z.array(LeaderboardEntrySchema),
  myRank: LeaderboardEntrySchema.nullable(),
  totalParticipants: z.number().int().nonnegative(),
  cachedAt: z.string().datetime(),
  ttlSeconds: z.number().int().positive(),
});

export type LeaderboardResponse = z.infer<typeof LeaderboardResponseSchema>;

// ============================================================================
// Achievements
// ============================================================================

export const AchievementRaritySchema = z.enum(['common', 'uncommon', 'rare', 'epic', 'legendary']);

export type AchievementRarity = z.infer<typeof AchievementRaritySchema>;

export const AchievementDetailSchema = z.object({
  id: z.string().uuid(),
  code: z.string().max(50),
  name: z.string().max(100),
  description: z.string().max(500),
  category: z.string().max(50),
  rarity: AchievementRaritySchema,
  pointsValue: z.number().int().nonnegative(),
  iconUrl: z.string().url().nullable(),
  isSecret: z.boolean(),
  totalUnlocked: z.number().int().nonnegative(), // park-wide count
  createdAt: z.string().datetime(),
});

export type AchievementDetail = z.infer<typeof AchievementDetailSchema>;

export const CustomerAchievementSchema = z.object({
  achievement: AchievementDetailSchema,
  unlockedAt: z.string().datetime(),
  pointsAwarded: z.number().int().nonnegative(),
  triggerType: z.enum(['automatic', 'manual', 'retroactive']),
});

export type CustomerAchievement = z.infer<typeof CustomerAchievementSchema>;

export const GetAchievementsQuerySchema = z.object({
  category: z.string().max(50).optional(),
  rarity: AchievementRaritySchema.optional(),
  unlockedOnly: BooleanFromString.optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetAchievementsQuery = z.infer<typeof GetAchievementsQuerySchema>;

// ============================================================================
// Badges
// ============================================================================

export const BadgeDetailSchema = z.object({
  id: z.string().uuid(),
  code: z.string().max(50),
  name: z.string().max(100),
  description: z.string().max(500),
  category: z.string().max(50),
  iconUrl: z.string().url().nullable(),
  earnedAt: z.string().datetime().nullable(), // null if not yet earned
});

export type BadgeDetail = z.infer<typeof BadgeDetailSchema>;

// ============================================================================
// Challenges
// ============================================================================

export const ChallengeTypeSchema = z.enum(['daily', 'weekly', 'special', 'seasonal']);

export type ChallengeType = z.infer<typeof ChallengeTypeSchema>;

export const ChallengeDetailSchema = z.object({
  id: z.string().uuid(),
  code: z.string().max(50),
  name: z.string().max(100),
  description: z.string().max(500),
  type: ChallengeTypeSchema,
  pointsReward: z.number().int().nonnegative(),
  bonusRewards: z.array(z.object({
    type: z.string(),
    value: z.string(),
    description: z.string(),
  })).optional(),
  requirements: z.array(z.object({
    type: z.string(),
    count: z.number().int().positive(),
    description: z.string(),
  })),
  maxParticipants: z.number().int().positive().nullable(),
  currentParticipants: z.number().int().nonnegative(),
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime(),
  isActive: z.boolean(),
});

export type ChallengeDetail = z.infer<typeof ChallengeDetailSchema>;

export const CustomerChallengeProgressSchema = z.object({
  challenge: ChallengeDetailSchema,
  joinedAt: z.string().datetime(),
  currentProgress: z.number().int().nonnegative(),
  requiredProgress: z.number().int().positive(),
  progressPercentage: z.number().min(0).max(100),
  completedAt: z.string().datetime().nullable(),
  pointsAwarded: z.number().int().nonnegative().nullable(),
  completionRank: z.number().int().positive().nullable(),
});

export type CustomerChallengeProgress = z.infer<typeof CustomerChallengeProgressSchema>;

export const GetChallengesQuerySchema = z.object({
  type: ChallengeTypeSchema.optional(),
  isActive: BooleanFromString.optional(),
  joinedOnly: BooleanFromString.optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export type GetChallengesQuery = z.infer<typeof GetChallengesQuerySchema>;

export const JoinChallengeRequestSchema = z.object({
  challengeId: z.string().uuid(),
});

export type JoinChallengeRequest = z.infer<typeof JoinChallengeRequestSchema>;

// ============================================================================
// Points Redemption
// ============================================================================

export const RedeemPointsRequestSchema = z.object({
  customerId: z.string(),
  pointsToRedeem: z.number().int().positive(),
  redemptionType: z.enum([
    'discount',
    'free_ticket',
    'merchandise',
    'fnb',
    'fast_pass',
    'upgrade',
  ]),
  redemptionItemId: z.string().max(100).optional(),
  orderId: z.string().uuid().optional(),
});

export type RedeemPointsRequest = z.infer<typeof RedeemPointsRequestSchema>;

export const RedeemPointsResponseSchema = z.object({
  transactionId: z.string().uuid(),
  customerId: z.string(),
  pointsRedeemed: z.number().int().positive(),
  previousBalance: z.number().int().nonnegative(),
  newBalance: z.number().int().nonnegative(),
  redemptionType: z.string(),
  redemptionValueIdr: z.number().int().nonnegative(),
  voucherCode: z.string().optional(),
  timestamp: z.string().datetime(),
});

export type RedeemPointsResponse = z.infer<typeof RedeemPointsResponseSchema>;
